import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePurchases } from '../contexts/PurchasesContext';
import { localAnalytics } from '../utils/analytics';
import { ThemeToggle } from '../components/common/ThemeToggle';
import { SecondaryButton, FontText, SEOHead } from '../components/common';
import { Header } from '../components/Header';
import { Purchases } from '@revenuecat/purchases-js';

export const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { userId, signOut, isAuthenticated, isAnonymous, isCampaign } =
    useAuth();
  const { coinBalance } = usePurchases();

  const { isUserPremium } = usePurchases();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleContactSupport = () => {
    localAnalytics().logEvent('ProfileContactSupportClicked', {
      screen: 'Profile',
      action: 'ContactSupportClicked',
      userId,
    });

    window.open('mailto:book@nanoreads.com', '_blank');
  };

  const handleManageSubscription = async () => {
    try {
      localAnalytics().logEvent('ProfileManageSubscriptionClicked', {
        screen: 'Profile',
        action: 'ManageSubscriptionClicked',
        userId,
      });

      const newWindow = window.open('', '_blank');

      const customerInfo =
        await Purchases.getSharedInstance().getCustomerInfo();
      if (customerInfo.managementURL) {
        if (newWindow) {
          newWindow.location.href = customerInfo.managementURL;
        } else {
          window.location.href = customerInfo.managementURL;
        }
      } else {
        console.error('No management URL available');
        if (newWindow) {
          newWindow.location.href =
            'mailto:book@nanoreads.com?subject=Subscription Management';
        } else {
          window.location.href =
            'mailto:book@nanoreads.com?subject=Subscription Management';
        }
      }
    } catch (error) {
      console.error('Failed to get customer management URL:', error);
      localAnalytics().logEvent('ProfileManageSubscriptionError', {
        screen: 'Profile',
        action: 'ManageSubscriptionError',
        userId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      window.location.href =
        'mailto:book@nanoreads.com?subject=Subscription Management';
    }
  };

  const handleLogout = async () => {
    try {
      localAnalytics().logEvent('ProfileLogoutClicked', {
        screen: 'Profile',
        action: 'LogoutClicked',
        userId,
      });

      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      padding: 20,
    },
    content: {
      paddingTop: 20,
    },
    section: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
    },
    sectionTitle: {
      marginBottom: 15,
    },
  };

  return (
    <>
      <SEOHead
        title={t('profile')}
        description={t('profile_description')}
        canonical="/profile"
      />
      <Header
        isPremium={isUserPremium}
        isAuthenticated={isAuthenticated}
        isCampaign={isCampaign}
        isAnonymous={isAnonymous}
        userCoins={coinBalance}
        menuOpen={menuOpen}
        onMenuToggle={() => setMenuOpen(!menuOpen)}
      />
      <div style={styles.container}>
        <div style={styles.content}>
          <FontText h2 style={{ marginBottom: 30 }}>
            {t('profile')}
          </FontText>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <FontText h3 style={{ color: theme.colors.text }}>
                {t('settings')}
              </FontText>
            </div>
            <ThemeToggle />
          </div>

          {isUserPremium && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <FontText h3 style={{ color: theme.colors.text }}>
                  {t('subscription')}
                </FontText>
              </div>
              <SecondaryButton
                onClick={handleManageSubscription}
                style={{
                  width: '100%',
                  padding: 15,
                  marginBottom: 10,
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderRadius: 8,
                }}
              >
                <FontText normal style={{ color: 'inherit', margin: 0 }}>
                  {t('manage_subscription')}
                </FontText>
                <FontText
                  tiny
                  style={{
                    color: 'inherit',
                    margin: '4px 0 0 0',
                    opacity: 0.7,
                  }}
                >
                  {t('manage_subscription_description')}
                </FontText>
              </SecondaryButton>
            </div>
          )}

          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <FontText h3 style={{ color: theme.colors.text }}>
                {t('support')}
              </FontText>
            </div>
            <SecondaryButton
              onClick={handleContactSupport}
              style={{
                width: '100%',
                padding: 15,
                marginBottom: 10,
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                borderRadius: 8,
              }}
            >
              <FontText normal style={{ color: 'inherit', margin: 0 }}>
                {t('contact_support')}
              </FontText>
              <FontText
                tiny
                style={{ color: 'inherit', margin: '4px 0 0 0', opacity: 0.7 }}
              >
                {t('contact_support_description')}
              </FontText>
            </SecondaryButton>
          </div>

          <SecondaryButton
            onClick={handleLogout}
            size="small"
            style={{ marginTop: 20 }}
          >
            <FontText style={{ color: 'inherit' }}>{t('sign_out')}</FontText>
          </SecondaryButton>
        </div>
      </div>
    </>
  );
};
